import React, { memo, useMemo } from "react";
import { View, Text, StyleSheet } from "@react-pdf/renderer";
import { DisaggregatedMetric } from "../../services/types";
import { formatValue } from "../../utils/helpers";

interface PDFAverageChartProps {
    indicators: DisaggregatedMetric[] | null;
    title?: string;
    tab: string;
    metric: DisaggregatedMetric | undefined;
    icon?: React.FC | null
    metricId: number;
    barColor: string;
    reverseOrder: boolean;
    topFive?: boolean;
    sort?: string;
}

const styles = StyleSheet.create({
    container: { width: '48%' },
    barRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
    label: { width: 80, fontSize: 8, color: '#3A2E29' },
    value: { width: 20, fontFamily: 'Helvetica-Bold', fontSize: 8, color: '#3A2E29', marginLeft: 8 },
    barBackground: { flex: 1, height: 10, backgroundColor: '#FFFFFF', marginLeft: 4, position: 'relative' },
    barFill: { height: 10 },
    metricCard: {
        width: '48%',
        marginBottom: 16,
        paddingRight: 4,
    },
    metricValue: { fontSize: 20, fontWeight: 700 },
    metricTitle: { fontSize: 10, fontWeight: 700, color: '#3A2E29' },
    metricYear: { fontSize: 10, marginTop: 2, marginBottom: 8, color: '#3A2E29' },

    averageLine: {
        position: 'absolute',
        width: 1,
        backgroundColor: 'red',
    },
        averageLabel: {
        position: 'absolute',
        top: -18,
        fontFamily: 'Helvetica-Bold', 
        fontSize: 6,
        color: '#3A2E29',
        marginLeft: -20,
        width: 40,
        textAlign: 'center',
    },
});

const PDFAverageChart = memo(({ indicators, tab, metricId, barColor, reverseOrder, topFive, sort, metric, icon, title }: PDFAverageChartProps) => {
    const data = useMemo(() => {
        if (!indicators) return [];
        const indicatorData = indicators.find(d => d.id === metricId);
        if (!indicatorData) return [];
        const version = indicatorData.versions[0];
        if (!version) return [];

        const tallyMap = new Map<string, number>();
        version.disaggregations.forEach(d => {
            let value: number;
            switch (version.unit) {
                case 'integer':
                    value = version.value ? d.value / version.value : 0;
                    break;
                case 'percentage':
                case 'decimal':
                case 'ratio':
                    value = d.value;
                    break;
                default:
                    value = 0;
            }
            tallyMap.set(d.name.trim(), value);
        });

        let allValues = Array.from(tallyMap.entries()).map(([label, value]) => ({ label, value }));
        if (sort === 'alphabetical') {
            allValues = allValues.sort((a, b) => a.label.localeCompare(b.label));
        }
        if (topFive) {
            allValues = allValues.sort((a, b) => b.value - a.value).slice(0, 6);
        }
        if (reverseOrder) {
            allValues.reverse();
        }
        return allValues;
    }, [indicators, tab, metricId, reverseOrder, topFive, sort]);

    if (!metric) return;

    const { name, versions } = metric;
    const { year } = versions[0];

    const maxValue = Math.max(...data.map(d => d.value)) || 1;
    const avgPercent = versions[0]?.value ? versions[0].value * 100 : 0;
    const avgValue = versions[0]?.value ?? 0;
    const normalizedAvg = (avgValue / maxValue) * 100;

    return (
        <View style={styles.metricCard}>
            {icon && React.createElement(icon)}
            <Text style={styles.metricTitle}>{title ?? name}</Text>
            <Text style={styles.metricYear}>in {year}</Text>
            {data.map((d, index) => {
	const isFirst = index === 0;
	const isLast = index === data.length - 1;

	// Total vertical space to cover (bar height + margin gap between bars)
	const lineHeight = isFirst || isLast ? 18 : 24;
	const lineTop = isFirst ? -10 : isLast ? -4 : -10;

	return (
		<View key={d.label} style={styles.barRow}>
			<Text style={styles.label}>{d.label}</Text>
            <Text style={styles.value}>{formatValue(d.value, 'percentage', metric.id)}</Text>
			<View style={styles.barBackground}>
				<View style={[styles.barFill, { width: `${(d.value / maxValue) * 100}%`, backgroundColor: barColor }]} />

				{/* Vertical line for average */}
				{versions[0]?.value !== undefined && (
					<>
						<View
							style={[
								styles.averageLine,
								{
									left: `${normalizedAvg}%`,
									top: lineTop,
									height: lineHeight,
								},
							]}
						/>
						{isFirst && (
							<Text
								style={[
									styles.averageLabel,
									{ left: `${normalizedAvg}%` },
								]}
							>
								{`${Math.round(avgPercent)}% Overall`}
							</Text>
						)}
					</>
				)}
			</View>
		</View>
	);
})}
        </View>
    );
});

export default PDFAverageChart;